<template>
  <!-- modern themed toggle. v-model'd boolean. wraps a hidden native
       checkbox so screen readers + keyboards still work via the label
       trick (clicking the track flips the checkbox, which flips the
       boolean). steelblue active state matches the rest of the Ops
       Desk surface. larger than b-form-checkbox switch on purpose —
       this is the primary affordance for the cell it sits in. -->
  <!-- ARIA switch: the root span IS the control (role=switch + tabindex),
       so it's reachable by Tab and toggled with Space/Enter, not just the
       mouse. no hidden native input — it added nothing and made the
       :focus-visible ring dead code (tabindex=-1 can't be focused). -->
  <span
    class="od-toggle"
    role="switch"
    :class="[sizeClass, { 'od-toggle--on': value, 'od-toggle--off': !value, 'od-toggle--disabled': disabled }]"
    :tabindex="disabled ? -1 : 0"
    :aria-checked="value ? 'true' : 'false'"
    :aria-disabled="disabled ? 'true' : 'false'"
    :aria-label="ariaLabel"
    @click="toggle"
    @keydown.space.prevent="toggle"
    @keydown.enter.prevent="toggle"
  >
    <span class="od-toggle-track">
      <span class="od-toggle-knob"></span>
    </span>
    <span v-if="label" class="od-toggle-label">{{ label }}</span>
  </span>
</template>

<script>
export default {
  name: 'od_toggle',
  props: {
    value: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    label: { type: String, default: '' },
    ariaLabel: { type: String, default: 'Toggle' },
    size: { type: String, default: 'md' }, // 'sm' | 'md' | 'lg'
  },
  computed: {
    sizeClass() {
      return 'od-toggle--' + (this.size || 'md')
    },
  },
  methods: {
    toggle() {
      if (this.disabled) return
      this.$emit('input', !this.value)
    },
  },
}
</script>

<style scoped>
.od-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  margin: 0;
}
.od-toggle--disabled { cursor: not-allowed; opacity: 0.55; }
.od-toggle:focus { outline: none; }

.od-toggle-track {
  position: relative;
  display: inline-block;
  background: #d6dde6;
  border-radius: 999px;
  transition: background 0.2s ease;
  flex-shrink: 0;
}
.od-toggle-knob {
  position: absolute;
  top: 50%;
  left: 3px;
  background: #fff;
  border-radius: 50%;
  transform: translateY(-50%);
  transition: left 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
}

/* md (default): ~larger than b-form-checkbox switch but still compact */
.od-toggle--md .od-toggle-track { width: 44px; height: 24px; }
.od-toggle--md .od-toggle-knob { width: 18px; height: 18px; }
.od-toggle--md.od-toggle--on .od-toggle-knob { left: 23px; }

.od-toggle--lg .od-toggle-track { width: 56px; height: 30px; }
.od-toggle--lg .od-toggle-knob { width: 24px; height: 24px; }
.od-toggle--lg.od-toggle--on .od-toggle-knob { left: 29px; }

.od-toggle--sm .od-toggle-track { width: 34px; height: 18px; }
.od-toggle--sm .od-toggle-knob { width: 13px; height: 13px; }
.od-toggle--sm.od-toggle--on .od-toggle-knob { left: 18px; }

.od-toggle--on .od-toggle-track { background: var(--fi-rail-accent); }
.od-toggle--on:hover .od-toggle-track { background: var(--fi-ops-accent); }
.od-toggle--off:hover .od-toggle-track { background: #b8c1cb; }
.od-toggle--disabled .od-toggle-track { background: #d6dde6 !important; }

.od-toggle-label {
  font-size: 13px;
  color: #1d1d1f;
  font-weight: 500;
}

/* focus ring: keyboard focus on the switch renders a halo on the track.
   mouse clicks don't trigger :focus-visible, so the ring only shows for
   keyboard users. */
.od-toggle:focus-visible .od-toggle-track {
  box-shadow: 0 0 0 3px rgba(70, 130, 180, 0.25);
}
</style>
